import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { uploadMediaFile } from '@/hooks/useUpload';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { fetchCommerceCategories, type CommerceCategory } from '@/lib/marketplaceCategories';
import { fetchAnnonceGallery, saveAnnonceGallery, parseMediaList } from '@/lib/marketplaceCatalog';
import type { ZifekUser } from '@/hooks/useAuth';

export interface AnnonceItem {
  id: number;
  titre: string;
  type: string | null;
  description: string | null;
  prix: string | null;
  ville: string | null;
  pays?: string | null;
  telephone: string | null;
  image: string | null;
  /** Vidéos additionnelles, séparées par « | ». */
  video?: string | null;
  publique: number;
  idcategorie: number | null;
  /** Annonce mise « à la une » par le vendeur (visible sur sa boutique). */
  is_vendor_featured?: boolean | null;
  /** Disponibilité déclarée par le vendeur (false = rupture / non disponible). */
  disponible?: boolean | null;
  created: string | null;
}

interface VendorProductFormProps {
  user: ZifekUser;
  marketplaceId?: number;
  editing: AnnonceItem | null;
  onClose: () => void;
  onSaved: () => void;
  /** Nature pré-sélectionnée : produits / services / annonces. */
  defaultType?: string;
  /** Catégorie principale déclarée par le vendeur (pré-remplie). */
  vendorCategoryId?: number | null;
  vendorCategoryNom?: string | null;
}

type Nature = 'produits' | 'services' | 'annonces';

/** Nature d'un contenu à partir de son type enregistré. */
function natureOf(type?: string | null): Nature {
  const t = (type || '').trim().toLowerCase();
  if (t === 'produits' || t === 'produit') return 'produits';
  if (t === 'services' || t === 'service') return 'services';
  return 'annonces';
}

const NATURES: Record<Nature, { label: string; icon: string; tone: string; hint: string }> = {
  produits: {
    label: 'Produit',
    icon: 'ri-shopping-bag-3-line',
    tone: 'bg-primary-100 text-primary-700',
    hint: 'Un produit est un article vendu avec un prix et une image de couverture.',
  },
  services: {
    label: 'Service',
    icon: 'ri-service-line',
    tone: 'bg-secondary-100 text-secondary-900',
    hint: 'Un service est une prestation proposée à vos clients.',
  },
  annonces: {
    label: 'Annonce',
    icon: 'ri-megaphone-line',
    tone: 'bg-accent-100 text-accent-900',
    hint: 'Une annonce est une mise en relation (immobilier, véhicules, emploi…).',
  },
};

/** Sous-types d'annonce (annonces classiques). */
const CLASSIFIED_TYPES: { value: string; label: string }[] = [
  { value: 'immobilier', label: 'Immobilier' },
  { value: 'vehicules', label: 'Véhicules' },
  { value: 'emploi', label: 'Emploi' },
  { value: 'electronique', label: 'Électronique' },
  { value: 'mode', label: 'Mode' },
  { value: 'maison', label: 'Maison' },
  { value: 'loisirs', label: 'Loisirs' },
  { value: 'annonce', label: 'Autre annonce' },
];

function titleFor(nature: Nature, editing: boolean): string {
  if (nature === 'produits') return editing ? 'Modifier le produit' : 'Nouveau produit';
  if (nature === 'services') return editing ? 'Modifier le service' : 'Nouveau service';
  return editing ? 'Modifier l\u2019annonce' : 'Nouvelle annonce';
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function nextYearISO(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

/** Extrait un message lisible depuis une erreur inconnue (les erreurs du backend ne sont pas des Error). */
function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === 'object') {
    const obj = err as { message?: string; details?: string; hint?: string; error_description?: string };
    return obj.message || obj.details || obj.hint || obj.error_description || '';
  }
  return '';
}

export default function VendorProductForm({
  user,
  marketplaceId,
  editing,
  onClose,
  onSaved,
  defaultType,
  vendorCategoryId,
  vendorCategoryNom,
}: VendorProductFormProps) {
  // La nature est figée : elle dépend de la page depuis laquelle on ouvre le
  // modal (Mes produits → produit, Mes services → service, etc.).
  const nature: Nature = useMemo(
    () => natureOf(editing?.type ?? defaultType),
    [editing?.type, defaultType],
  );

  const [classifType, setClassifType] = useState<string>(() => {
    const t = (editing?.type ?? '').trim().toLowerCase();
    return CLASSIFIED_TYPES.some((c) => c.value === t) ? t : 'annonce';
  });
  const [titre, setTitre] = useState(editing?.titre ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [prix, setPrix] = useState(editing?.prix ?? '');
  const [image, setImage] = useState(editing?.image ?? '');
  const [gallery, setGallery] = useState<string[]>([]);
  const [videos, setVideos] = useState<string[]>(() => parseMediaList(editing?.video));
  const [publique, setPublique] = useState(editing?.publique ?? 1);
  // Disponibilité : le vendeur peut déclarer un produit/service en rupture
  // de stock ou non disponible (masque la commande côté clients).
  const [disponible, setDisponible] = useState<boolean>(editing?.disponible !== false);
  const [rootCatId, setRootCatId] = useState<string>('');
  const [subCatId, setSubCatId] = useState<string>('');
  const [subSubCatId, setSubSubCatId] = useState<string>('');

  const [categories, setCategories] = useState<CommerceCategory[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);

  // Les annonces (classées) n'accueillent que des images, les produits &
  // services acceptent en plus de courtes vidéos.
  const allowVideo = nature !== 'annonces';

  // Catégories strictement issues de CETTE marketplace (isolées par commerce).
  useEffect(() => {
    let active = true;
    (async () => {
      if (!marketplaceId) {
        setCategories([]);
        return;
      }
      const cats = await fetchCommerceCategories(marketplaceId);
      if (!active) return;
      setCategories(cats.filter((c) => c.afficher === 1));
    })();
    return () => { active = false; };
  }, [marketplaceId]);

  // Charge la galerie photos additionnelle en édition.
  useEffect(() => {
    let active = true;
    if (!editing?.id) {
      setGallery([]);
      return;
    }
    setLoadingGallery(true);
    fetchAnnonceGallery(editing.id)
      .then((urls) => { if (active) setGallery(urls); })
      .finally(() => { if (active) setLoadingGallery(false); });
    return () => { active = false; };
  }, [editing?.id]);

  // Racines (parent_id nul) + sous-catégories de la catégorie choisie.
  const rootCategories = useMemo(() => categories.filter((c) => c.parent_id == null), [categories]);
  const subCategories = useMemo(
    () => (rootCatId ? categories.filter((c) => Number(c.parent_id) === Number(rootCatId)) : []),
    [categories, rootCatId],
  );
  const subSubCategories = useMemo(
    () => (subCatId ? categories.filter((c) => Number(c.parent_id) === Number(subCatId)) : []),
    [categories, subCatId],
  );

  // Initialise la sélection une fois les catégories chargées : on pré-remplit
  // avec la catégorie du vendeur ou celle du contenu édité, en séparant la
  // catégorie parente et la sous-catégorie.
  useEffect(() => {
    if (categories.length === 0) return;
    const byId = (id: number) => categories.find((c) => Number(c.id) === id);
    const initialRaw =
      editing?.idcategorie != null
        ? Number(editing.idcategorie)
        : vendorCategoryId != null
          ? Number(vendorCategoryId)
          : null;
    if (initialRaw == null || !Number.isFinite(initialRaw)) return;
    let node = byId(initialRaw);
    if (!node) return;
    // Remonte la chaîne des ancêtres : racine → sous → sous-sous.
    const chain: CommerceCategory[] = [node];
    while (node && node.parent_id != null) {
      const parent = byId(Number(node.parent_id));
      if (!parent) break;
      chain.unshift(parent);
      node = parent;
    }
    setRootCatId(chain[0] ? String(chain[0].id) : '');
    setSubCatId(chain[1] ? String(chain[1].id) : '');
    setSubSubCatId(chain[2] ? String(chain[2].id) : '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadMediaFile(file, 'annonces');
      setImage(url);
    } catch (err) {
      setError(errorMessage(err) || "Échec de l'upload de l'image");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setError('');
    try {
      const urls: string[] = [];
      for (const file of files) {
        const url = await uploadMediaFile(file, 'annonces');
        urls.push(url);
      }
      setGallery((prev) => [...prev, ...urls]);
    } catch (err) {
      setError(errorMessage(err) || "Échec de l'upload des images");
    } finally {
      setUploading(false);
      if (galleryRef.current) galleryRef.current.value = '';
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setError('');
    try {
      const urls: string[] = [];
      for (const file of files) {
        if (!file.type.startsWith('video/')) continue;
        const url = await uploadMediaFile(file, 'annonces-videos');
        urls.push(url);
      }
      setVideos((prev) => [...prev, ...urls]);
    } catch (err) {
      setError(errorMessage(err) || "Échec de l'upload de la vidéo");
    } finally {
      setUploading(false);
      if (videoRef.current) videoRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titre.trim()) {
      setError('Le titre est obligatoire.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Le type enregistré dépend de la nature (figée) du contenu.
      const type = nature === 'produits' ? 'produits' : nature === 'services' ? 'services' : (classifType || 'annonce');

      // Pays & ville du compte vendeur : enregistrés pour chaque contenu,
      // même s'ils ne sont pas affichés dans ce formulaire.
      const pays = editing?.pays ?? user["Pays"] ?? '';
      const ville = editing?.ville ?? user["Ville"] ?? '';
      // Le téléphone du compte sert de contact (champ retiré du formulaire).
      const telephone = editing?.telephone ?? user.telephone ?? '';

      // Chaîne des catégories (jusqu'à 3 niveaux). On enregistre la catégorie
      // la plus profonde + son parent + son grand-parent, afin que le contenu
      // apparaisse dans toute la hiérarchie (racine → sous → sous-sous).
      const finalCategoryId = subSubCatId
        ? Number(subSubCatId)
        : subCatId
          ? Number(subCatId)
          : rootCatId
            ? Number(rootCatId)
            : null;
      const parentCategoryId = subSubCatId
        ? (subCatId ? Number(subCatId) : null)
        : subCatId
          ? (rootCatId ? Number(rootCatId) : null)
          : null;
      const grandparentCategoryId = subSubCatId && rootCatId ? Number(rootCatId) : null;

      // Colonnes obligatoires historiques du schéma (sans valeur par défaut) :
      // à fournir explicitement pour éviter l'échec de l'enregistrement.
      const payload = {
        titre: titre.trim(),
        type,
        description: description.trim(),
        prix: prix.trim(),
        ville,
        pays,
        telephone,
        image: image.trim(),
        video: videos.join('|'),
        date_debut: todayISO(),
        date_fin: nextYearISO(),
        ip: '',
        visibilite: '',
        sp: '',
        pourcentage: '',
        click: '',
        publique: Number(publique),
        disponible,
        idcategorie: finalCategoryId,
        idcategorie_parent: parentCategoryId,
        idcategorie_grandparent: grandparentCategoryId,
        owner: user.nomcommerce || user.name,
        idvendeur: user.id,
      };

      let annonceId = editing?.id ?? 0;
      if (editing) {
        const { error: upErr } = await supabase.from('annonce').update(payload).eq('id', editing.id);
        if (upErr) throw upErr;
      } else {
        const { data: inserted, error: insErr } = await supabase
          .from('annonce')
          .insert(payload)
          .select('id')
          .maybeSingle();
        if (insErr) throw insErr;
        annonceId = Number(inserted?.id || 0);
      }

      // Galerie photos additionnelle (produits, services et annonces).
      if (annonceId > 0) {
        await saveAnnonceGallery(annonceId, gallery);
      }
      onSaved();
    } catch (err) {
      setError(errorMessage(err) || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const natureMeta = NATURES[nature];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : onClose} />
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-bold font-heading text-foreground-950">
            {titleFor(nature, !!editing)}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="space-y-4">
          {/* Nature figée : rappel uniquement (pas de mélange produit/service/annonce) */}
          <div className={`px-3.5 py-2.5 rounded-lg flex items-center gap-2.5 ${natureMeta.tone}`}>
            <i className={`${natureMeta.icon} text-base`}></i>
            <div className="min-w-0">
              <div className="text-xs font-semibold">{natureMeta.label}</div>
              <div className="text-[11px] opacity-80 truncate">{natureMeta.hint}</div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
              Titre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder={
                nature === 'produits' ? 'Ex : Canapé 3 places en tissu'
                  : nature === 'services' ? 'Ex : Coiffure à domicile'
                    : 'Ex : Appartement 2 chambres à louer'
              }
              className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
            />
          </div>

          {/* Sous-type d'annonce (uniquement pour les annonces) */}
          {nature === 'annonces' && (
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Sous-type d&apos;annonce</label>
              <select
                value={classifType}
                onChange={(e) => setClassifType(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                {CLASSIFIED_TYPES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Prix</label>
            <input
              type="text"
              value={prix}
              onChange={(e) => setPrix(e.target.value)}
              placeholder="Ex : 250"
              className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
            />
          </div>

          {/* Catégorie (racines) + sous-catégories + sous-sous-catégories */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Catégorie</label>
              <select
                value={rootCatId}
                onChange={(e) => { setRootCatId(e.target.value); setSubCatId(''); setSubSubCatId(''); }}
                className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="">Sélectionner une catégorie…</option>
                {rootCategories.map((c) => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
              {vendorCategoryNom && (
                <p className="mt-1.5 text-[11px] text-foreground-400 flex items-center gap-1.5">
                  <i className="ri-price-tag-3-line"></i>
                  Votre catégorie de vendeur : <strong>{vendorCategoryNom}</strong>
                </p>
              )}
              {categories.length === 0 && (
                <p className="mt-1.5 text-[11px] text-foreground-400">
                  La marketplace n&apos;a pas encore créé de catégories.
                </p>
              )}
            </div>
            {subCategories.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Sous-catégorie</label>
                <select
                  value={subCatId}
                  onChange={(e) => { setSubCatId(e.target.value); setSubSubCatId(''); }}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Aucune</option>
                  {subCategories.map((c) => (
                    <option key={c.id} value={c.id}>{c.nom}</option>
                  ))}
                </select>
              </div>
            )}
            {subSubCategories.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Sous-sous-catégorie</label>
                <select
                  value={subSubCatId}
                  onChange={(e) => setSubSubCatId(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Aucune</option>
                  {subSubCategories.map((c) => (
                    <option key={c.id} value={c.id}>{c.nom}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Décrivez votre produit, service ou annonce..."
              className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
            />
          </div>

          {/* Pays & ville hérités du compte vendeur (non modifiables ici) */}
          {(user["Pays"] || user["Ville"]) && (
            <div className="px-3.5 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-[11px] text-foreground-500 flex items-center gap-2">
              <i className="ri-map-pin-2-line"></i>
              Enregistré avec votre localisation : {[user["Ville"], user["Pays"]].filter(Boolean).join(', ')}
            </div>
          )}

          {/* Image de couverture */}
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Image de couverture</label>
            <div className="flex items-center gap-3">
              {image ? (
                <div className="w-16 h-16 rounded-lg overflow-hidden border border-background-200/70 flex-shrink-0">
                  <img src={resolveMediaUrl(image)} alt="" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-image-line text-foreground-300"></i>
                </div>
              )}
              <div className="flex-1">
                <input ref={fileRef} type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="px-3.5 py-2 rounded-lg border border-background-200/70 bg-background-50 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
                >
                  <i className={uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-line'}></i>
                  {uploading ? 'Upload...' : image ? "Changer l'image" : 'Choisir une image'}
                </button>
              </div>
            </div>
          </div>

          {/* Galerie : plusieurs images additionnelles */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold text-foreground-600">Images additionnelles</label>
              <span className="text-[10px] text-foreground-400">{gallery.length} image{gallery.length > 1 ? 's' : ''}</span>
            </div>
            {loadingGallery ? (
              <div className="py-3 text-center">
                <i className="ri-loader-4-line animate-spin text-foreground-400"></i>
              </div>
            ) : (
              <>
                {gallery.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {gallery.map((url, i) => (
                      <div key={`${url}-${i}`} className="relative aspect-square rounded-lg overflow-hidden border border-background-200/70 group">
                        <img src={resolveMediaUrl(url)} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setGallery((prev) => prev.filter((_, idx) => idx !== i))}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/55 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          title="Retirer"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  ref={galleryRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => galleryRef.current?.click()}
                  disabled={uploading}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-dashed border-background-300/70 bg-background-50 text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <i className="ri-add-line"></i>
                  Ajouter des images
                </button>
              </>
            )}
          </div>

          {/* Vidéos courtes (produits & services uniquement) */}
          {allowVideo && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-foreground-600">Vidéos courtes</label>
                <span className="text-[10px] text-foreground-400">{videos.length} vidéo{videos.length > 1 ? 's' : ''}</span>
              </div>
              {videos.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {videos.map((url, i) => (
                    <div key={`${url}-${i}`} className="relative aspect-video rounded-lg overflow-hidden border border-background-200/70 bg-black group">
                      <video src={resolveMediaUrl(url)} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                      <button
                        type="button"
                        onClick={() => setVideos((prev) => prev.filter((_, idx) => idx !== i))}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        title="Retirer"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input
                ref={videoRef}
                type="file"
                accept="video/mp4,video/webm"
                multiple
                onChange={handleVideoUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => videoRef.current?.click()}
                disabled={uploading}
                className="w-full px-3.5 py-2.5 rounded-lg border border-dashed border-background-300/70 bg-background-50 text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <i className="ri-video-add-line"></i>
                Ajouter des vidéos courtes
              </button>
            </div>
          )}

          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={Number(publique) === 1}
              onChange={(e) => setPublique(e.target.checked ? 1 : 0)}
              className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
            />
            <span className="text-sm text-foreground-700">Publier (visible par les clients)</span>
          </label>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={!disponible}
              onChange={(e) => setDisponible(!e.target.checked)}
              className="w-4 h-4 rounded accent-red-500 cursor-pointer"
            />
            <span className="text-sm text-foreground-700">
              En rupture de stock / non disponible
              <span className="block text-[11px] text-foreground-400">Le contenu reste visible mais ne peut pas être commandé.</span>
            </span>
          </label>

          {error && (
            <div className="px-3.5 py-2.5 rounded-lg bg-red-50 text-red-600 text-xs flex items-center gap-2">
              <i className="ri-error-warning-line"></i>
              {error}
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 py-2.5 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              className="flex-1 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-2"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i>Enregistrement...</>
              ) : (
                <><i className="ri-save-line"></i>{editing ? 'Enregistrer' : 'Publier'}</>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}